import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import {
  FileTranslationsManagerUpload
} from "@shared/models/file-translations-manager/file-translations-manager-upload.model";
import {
  FileTranslationsUploadOptions,
  FileTranslationsUploadType, FileTypes,
  FileUploadErrorMessages,
  MAX_SELECTABLE_FILES
} from '@app/shared/constants/file-translations/file-translations-upload-options';
import { FileTranslationsManagerService } from "@services/file-translations/file-manager/file-translations-manager.service";
import { FileTranslationsManagerTranslate } from "@shared/models/file-translations-manager/file-translations-manager-translate.model";
import { FileTranslationsProcessingStatus } from "@shared/constants/file-translations/file-translations-processing-status"
import { FileTranslationValidationService } from '@app/core/services/file-translations/file-translation-validation/file-translation-validation.service';
import { FileTranslationsUploadStatus } from "@shared/constants/file-translations/file-translations-upload-status";
import { KmdModalService } from "gds-atom-components";
import { FileTranslation } from "@shared/models/file-translation.model";
import { filter, Subject, takeUntil } from "rxjs";
import { detectLanguage } from '@services/file-translations/file-translation-validation/language-detection/language-detection'
import { extractTextFromFile } from "@services/file-translations/file-translation-validation/text-from-file-extraction/extract-text-from-file";
import { firstValueFrom } from "rxjs";
import { LANGUAGE_DATA, LanguageManagerService } from "@services/file-translations/language-manager/language-manager.service";
import { NavigationStart, Router } from '@angular/router';

export interface FileUploadEvent {
  type: 'uploaded' | 'error' | 'removed' | 'uploading';
  data?: FileTranslationsManagerUpload;
}

export interface FileTranslateEvent {
  type: 'translated' | 'error' | 'removed' | 'translating';
  data?: FileTranslationsManagerTranslate;
}

@Component({
  selector: 'app-file-translations-uploader',
  templateUrl: './file-translations-uploader.component.html',
  styleUrl: './file-translations-uploader.component.css',
  animations: [
    trigger('fileListAnimation', [
      transition('* => *', [
        query(':enter', [
          style({ transform: 'translateY(-20px)', opacity: 0 }),
          stagger('100ms', animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })))
        ], { optional: true }),
        query(':leave', [
          stagger('100ms', animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 })))
        ], { optional: true })
      ])
    ])
  ]
})
export class FileTranslationsUploaderComponent implements OnInit, OnDestroy {

  @Output() onUploading = new EventEmitter<string[]>();
  @Output() onTranslating = new EventEmitter<boolean>();
  @Output() translationComplete = new EventEmitter<FileTranslation>();
  @Output() onLanguageDetected = new EventEmitter<{notificationId: string,
    detectedLanguage: string, detectedLanguageCode: string, isSupported: boolean, isMatch: boolean}>();
  @Output() onFileUploadErrorAlert = new EventEmitter<{showError: boolean, errorMessage: string, errorTitle?: string}>();
  @Output() onFileTranslatedRefreshTranslatedList = new EventEmitter<boolean>();
  @Output() onErrorLanguageFileRemoved = new EventEmitter<{notificationId: string}>();
  @Output() onSwitchToTextTab = new EventEmitter<void>();
  @Output() onInsufficientTextDetected = new EventEmitter<{ notificationId: string, fileName: string}>();
  @Output() onPasswordProtectedDetected = new EventEmitter<{ notificationId: string }>();

  @Input() targetLanguage: string = "";
  @Input() sourceLanguage: string = "";
  @Input() destroy$: Subject<boolean> = new Subject<boolean>();

  readonly uploadStatus = FileTranslationsUploadStatus;

  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('filesContainer') filesContainer!: ElementRef;

  isUploading = false
  isTranslating = false
  readonly uploadType: FileTranslationsUploadType = FileTranslationsUploadOptions['TRANSLATIONS'];
  readonly fileTypes = FileTypes['TRANSLATIONS'];
  uploadFiles: FileTranslationsManagerUpload[] = [];
  translateFiles: FileTranslationsManagerTranslate[] = [];
  activeTranslatingCount = 0;
  activeUploadingCount = 0;
  newBatchUploaded: boolean = false;

  constructor(
    private fileTranslationsManagerService: FileTranslationsManagerService,
    private fileTranslationsValidationService: FileTranslationValidationService,
    private languageManagerService: LanguageManagerService,
    private kmdModalService: KmdModalService,
    private router: Router
  ) {
    this.router.events.pipe(
      takeUntil(this.destroy$),
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => {
      this.fileTranslationsManagerService.clearSelectedFiles();
    });
  }

  ngOnInit() {
    this.fileTranslationsManagerService.initUploadFiles(this.uploadFiles);
    this.fileTranslationsManagerService.initTranslateFiles(this.translateFiles, this.destroy$,
        () => this.onTranslatingInProgress());
    this.resetSourceLanguage();
    this.resetTargetLanguage();
    this.activeTranslatingCount = this.getFilesInTranslationCount();
    this.activeUploadingCount = this.getFilesInUploadCount();
  }

  ngOnDestroy() {
    this.fileTranslationsManagerService.cleanupCancellations();
    this.fileTranslationsManagerService.clearSelectedFiles();
  }

  scrollFilesContainerToBottom(): void {
    this.filesContainer.nativeElement.scrollTop = this.filesContainer.nativeElement.scrollHeight;
  }

  scrollFilesContainerToTop(): void {
    this.filesContainer.nativeElement.scrollTop = 0;
  }

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  };

  onFileChange(event: any): void {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      this.fileInput.nativeElement.value = '';
      this.handleFileUpload(filesArray);
    }
  }

  handleUploadEvents(event: FileUploadEvent): void {
    switch (event.type) {
      case 'uploaded':
        if(event.data){
          this.onFileUploaded(event.data);
        }
        break;
      case 'removed':
        if(event.data && event.data.notificationId){
            this.onErrorLanguageFileRemoved.emit({notificationId: event.data.notificationId});
        }
        this.onUploadingInProgress();
        break;
      case 'error':
      case 'uploading':
        this.onUploadingInProgress();
        break;
    }
  }

  handleTranslateEvents(event: FileTranslateEvent): void {
    switch (event.type) {
      case 'translated':
        if(event.data){
          this.onFileTranslated(event.data);
        }
        break;
      case 'error':
      case 'removed':
      case 'translating':
        this.onTranslatingInProgress();
        break;
    }
  }

  async handleFileUpload(files: FileList | File[]): Promise<void> {
    const filesArray = Array.isArray(files) ? files : Array.from(files);

    const filesInProcessingArea = this.uploadFiles.length + this.translateFiles.length;

    if (filesArray.length > MAX_SELECTABLE_FILES) {
      this.handleError(FileUploadErrorMessages.FILE_UPLOAD_FILE_LIMIT_EXCEEDED_TEXT);
      return;
    }

    if (filesInProcessingArea + filesArray.length > MAX_SELECTABLE_FILES) {
      this.handleError(FileUploadErrorMessages.FILE_UPLOAD_FILE_LIMIT_EXCEEDED_TEXT);
      return;
    }

    const filesToUpload: File[] = [];

    for (let i = 0; i < filesArray.length; i++) {
      if (await this.validateFile(filesArray[i])) {
        filesToUpload.push(filesArray[i]);
      }
    }

    if (filesToUpload.length > 0) {
      this.newBatchUploaded = true;

      this.fileTranslationsManagerService.uploadFiles(
        filesToUpload,
        this.uploadType,
        this.sourceLanguage,
        this.targetLanguage,
        (errorMessage?: string) => this.handleError(errorMessage)
      ).forEach((fileUpload) => {
        const upload = this.fileTranslationsManagerService.getFileUploadStatus(fileUpload);
        this.uploadFiles.push(upload);
        this.onUploadingInProgress();
      });
    }
  }

  async validateFile(file: File): Promise<boolean> {
    if (!file || !(file instanceof File)) {
      this.handleError('Invalid file received.');
      return false;
    }
    const fileUpload: FileTranslationsManagerUpload = {
      file: file
    };

    // Check File Type
    let fileType = this.fileTranslationsValidationService.getFileType(file);
    if (fileType === '' || !this.fileTranslationsValidationService.isValidFileType(fileType)) {
      this.handleError(FileUploadErrorMessages.FILE_UPLOAD_INVALID_FILE_TYPE);
      return false;
    }

    // Check file size (Max limit Based on fileType)
    if (this.fileTranslationsValidationService.isMaxFileSizeExceeded(file.size, fileType)) {
      fileUpload.status = FileTranslationsUploadStatus.FILE_SIZE_EXCEEDED;
      this.uploadFiles.push(fileUpload);
      return false;
    }
    const notificationId = crypto.randomUUID();

    // Check if file is password protected
    if (await this.fileTranslationsValidationService.isFilePasswordProtected(file, file.type)) {
      fileUpload.status = FileTranslationsUploadStatus.PASSWORD_PROTECTED;
      this.uploadFiles.push(fileUpload);

      this.onPasswordProtectedDetected.emit({
        notificationId: notificationId
      });
      fileUpload.notificationId = notificationId;
      return false;
    }

    try {
      const text = await firstValueFrom(extractTextFromFile(file));

      // if type is pdf & text is empty or undefined -> Scanned Pdf
      if (fileType === this.fileTypes.PDF.toLowerCase()) {
        if (!text || text.trim() === '') {
          this.handleError(FileUploadErrorMessages.FILE_UPLOAD_SCANNED_PDF);
          return false;
        }
      }

      //Insufficient Text Content Catch
      if (text.length < 100) {
        fileUpload.status = FileTranslationsUploadStatus.INSUFFICIENT_TEXT_CONTENT;
        this.uploadFiles.push(fileUpload);

        this.onInsufficientTextDetected.emit({
          notificationId: notificationId,
          fileName: file.name
        });

        fileUpload.notificationId = notificationId;
        return false;
      }
      else {
        // Call language detection tool
        const detection_result = detectLanguage(text);

        const sourceLanguages = await firstValueFrom(this.languageManagerService.getSourceLanguages());
        const isSupported = sourceLanguages.some(language => language.code === detection_result.languageCode);
        const isMatch = this.checkIfLanguageMatches(this.sourceLanguage, detection_result.languageCode);
        const detectedLanguageDisplayName = this.getLanguageDisplayName(detection_result.languageCode);

        this.onLanguageDetected.emit({
          notificationId: notificationId,
          detectedLanguage: detectedLanguageDisplayName,
          detectedLanguageCode: detection_result.languageCode,
          isSupported: isSupported,
          isMatch: isMatch
        });

        fileUpload.notificationId = notificationId;
        if (!isSupported) {
          fileUpload.status = FileTranslationsUploadStatus.UNSUPPORTED_LANGUAGE;
          this.uploadFiles.push(fileUpload);
          return false;
        }

        if (!isMatch) {
          fileUpload.status = FileTranslationsUploadStatus.LANGUAGE_ERROR;
          this.uploadFiles.push(fileUpload);
          return false;
        }
      }
    } catch (error) {
      this.handleError('There was an error interpreting your file.' + error);
      return false;
    }

    return true;
  };

  checkIfLanguageMatches(sourceLanguage: string, detectedLanguageCode: string): boolean {
    if (sourceLanguage === detectedLanguageCode) {
      return true;
    }

    const languageGroups = [
      // Spanish variants
      ['es-ES', 'es-419'], //include ca (Catalan) & gl (Galician) if needed
      // Portuguese variants
      ['pt-PT', 'pt-BR'],
      // Persian / Dari
      ['fa', 'prs'],
      // Indonesian / Malay
      ['id', 'ms'],
      // Russian / Belarussian
      ['ru', 'be'],
      // Tagalog / Filipino
      ['tl', 'fil'],
      // Bosnian with Slovenian
      ['bs', 'sl'],
      // Serbian with Macedonian
      ['sr', 'mk-MK'],
      // Bulgarian / Macedonian
      ['bg', 'mk-MK', 'bg-BG'],
      // Azerbaijani / Turkish family
      ['az-Cyrl-AZ', 'az-Latn-AZ', 'tr']
    ];

    return languageGroups.some(group =>
      group.includes(sourceLanguage) && group.includes(detectedLanguageCode)
    );
  }

  handleError(errorMessage: string = FileUploadErrorMessages.FILE_UPLOAD_FAILED, errorTitle: string = 'Error:'): void {
    this.onFileUploadErrorAlert.emit({
      showError: true,
      errorMessage: errorMessage,
      errorTitle: errorTitle
    });
  }

  disableUploader(): boolean {
    return !(this.sourceLanguage && this.targetLanguage);
  }

  onFileUploaded(data: FileTranslationsManagerUpload): void {
    if (data && data.id) {
      const index = this.uploadFiles.indexOf(data);
      if (index !== -1) {
        this.uploadFiles.splice(index, 1);
      }
      const fileId = data.id;
      const translateData = this.fileTranslationsManagerService.getFileTranslateStatus(fileId, this.destroy$);
      this.translateFiles.push(translateData);
      this.onUploadingInProgress();
      this.onTranslatingInProgress();
      this.scrollFilesContainerToBottom();
    }
  }

  onFileTranslated(data: FileTranslationsManagerTranslate): void {
    if (data && data.file) {
      const index = this.translateFiles.indexOf(data);
      if (index !== -1) {
        this.translateFiles.splice(index, 1);
      }
      if (this.newBatchUploaded) {
        this.fileTranslationsManagerService.clearSelectedFiles();
        this.newBatchUploaded = false;
      }
      if (data.file && data.file.id) {
        this.fileTranslationsManagerService.addSelectedFile(data.file);
      }
      this.onTranslatingInProgress();
      this.scrollFilesContainerToTop();
      this.onFileTranslatedRefreshTranslatedList.emit(true);
    }
  }

  getFilesInTranslationCount(): number {
    return this.translateFiles
        .filter(item => item.status === FileTranslationsProcessingStatus.TRANSLATING
            && item.file !== undefined)
        .length;
  }

  getFilesInUploadCount(): number {
    return this.uploadFiles.filter(item => item.status === FileTranslationsUploadStatus.IN_PROGRESS
            && item.file !== undefined)
        .length;
  }

  getFileIdsInUpload() {
    return this.uploadFiles.filter(item => item.status === FileTranslationsUploadStatus.IN_PROGRESS
            && item.file !== undefined).map(item => item.id!);
  }

  onUploadingInProgress() {
    this.activeUploadingCount = this.getFilesInUploadCount();
    this.isUploading = this.activeUploadingCount > 0
    this.onUploading.emit(this.getFileIdsInUpload());
  }

  onTranslatingInProgress() {
    this.activeTranslatingCount = this.getFilesInTranslationCount();
    this.isTranslating = this.activeTranslatingCount > 0
    this.onTranslating.emit(this.isTranslating);
  }

  getLanguageDisplayName = (langCode: string): string => {
    const language = LANGUAGE_DATA.sourceLanguages.find(
      lang => lang.code === langCode
    );

    if (langCode === 'pt-PT' || langCode === 'pt-BR') {
      return 'Portuguese';
    }
    if (langCode === 'es-ES' || langCode === 'es-419') {
      return 'Spanish';
    }
    if (langCode === 'rw-RW' || langCode === 'rw') {
      return 'Kinyarwanda';
    }

    return language?.displayName || langCode;
  };

  switchToTextTab(): void {
    this.closeInsufficientTextModal();
    this.onSwitchToTextTab.emit();
  }

  closeInsufficientTextModal(): void {
    this.kmdModalService.close('insufficient-text-modal');
  }

  showClearFilesButton(): boolean {
    return this.uploadFiles.length > 0 || this.translateFiles.length > 0;
  }

  openClearFilesModal(): void {
    this.kmdModalService.open('clear-files-modal');
  }

  closeClearFilesModal(): void {
    this.kmdModalService.close('clear-files-modal');
  }

  showClearFilesModal(): void {
    if (this.isUploading || this.isTranslating) {
      this.openClearFilesModal();
    } else {
      this.clearAllFiles();
    }
  }

  clearAllFiles(): void {
    const uploadFilesCopy = [...this.uploadFiles];
    uploadFilesCopy.forEach(file => {
      if (file && file.cancel) {
        file.cancel();
      }
      if(file && file.notificationId){
        this.onErrorLanguageFileRemoved.emit({notificationId: file.notificationId});
      }
    });
    this.uploadFiles = [];

    const translateFilesCopy = [...this.translateFiles];
    translateFilesCopy.forEach(file => {
      if (file && file.cancel) {
        file.cancel();
      }
    });
    this.translateFiles = [];

    this.onUploadingInProgress();
    this.onTranslatingInProgress();
    this.closeClearFilesModal();
  }

  get wrapperClasses(): { [key: string]: boolean } {
    return {
      'has-files': this.hasFiles(),
      'single-file': this.singleFile()
    }
  };

  hasFiles(){
      return (this.uploadFiles.length + this.translateFiles.length) > 0;
  }

  singleFile(){
      return (this.uploadFiles.length || this.translateFiles.length) === 1;
  }

  resetSourceLanguage(){
      this.sourceLanguage = "";
  }

  resetTargetLanguage(){
      this.targetLanguage = "";
  }

}
